// TrustBadges.jsx — Trust strip with MUI icons + scrolling review marquee

import React from "react";
import { motion } from "framer-motion";

// MUI Icons
import LocalShippingOutlinedIcon    from "@mui/icons-material/LocalShippingOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import SecurityOutlinedIcon         from "@mui/icons-material/SecurityOutlined";
import StarOutlinedIcon             from "@mui/icons-material/StarOutlined";
import DeliveryDiningOutlinedIcon   from "@mui/icons-material/DeliveryDiningOutlined";
import CardGiftcardOutlinedIcon     from "@mui/icons-material/CardGiftcardOutlined";

const BADGES = [
  {
    Icon: LocalShippingOutlinedIcon,
    title: "Free Shipping",
    desc:  "On all orders over $50",
    color: "text-rose-500",
    bg:    "bg-rose-50",
  },
  {
    Icon: AssignmentReturnOutlinedIcon,
    title: "Easy Returns",
    desc:  "30-day hassle-free returns",
    color: "text-violet-500",
    bg:    "bg-violet-50",
  },
  {
    Icon: SecurityOutlinedIcon,
    title: "Secure Payment",
    desc:  "256-bit SSL encryption",
    color: "text-emerald-500",
    bg:    "bg-emerald-50",
  },
  {
    Icon: StarOutlinedIcon,
    title: "4.9★ Rated",
    desc:  "By 50,000+ customers",
    color: "text-amber-500",
    bg:    "bg-amber-50",
  },
  {
    Icon: DeliveryDiningOutlinedIcon,
    title: "Same-Day Delivery",
    desc:  "Available in select cities",
    color: "text-blue-500",
    bg:    "bg-blue-50",
  },
  {
    Icon: CardGiftcardOutlinedIcon,
    title: "Gift Wrapping",
    desc:  "Available on select items",
    color: "text-pink-500",
    bg:    "bg-pink-50",
  },
];

const REVIEWS = [
  { name: "Sarah M.",  review: "Incredible store! Fast delivery and beautiful products.",   rating: 5 },
  { name: "Raj K.",   review: "The handicrafts are absolutely stunning. 10/10!",            rating: 5 },
  { name: "Emily T.", review: "Same-day delivery was a lifesaver. Super impressed!",        rating: 5 },
  { name: "David L.", review: "Premium packaging and the quality is top-notch!",            rating: 5 },
  { name: "Priya S.", review: "Amazing toy selection. My kid was overjoyed!",               rating: 5 },
  { name: "Mike R.",  review: "The chess set exceeded all expectations.",                   rating: 5 },
  { name: "Ananya B.",review: "Fast shipping, great prices. Highly recommended!",          rating: 5 },
];

const containerVariants = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.08 } },
};
const badgeVariants = {
  hidden:  { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

export default function TrustBadges() {
  return (
    <section className="py-14 px-4 bg-white border-t border-b border-gray-100">
      <div className="max-w-7xl mx-auto">

        {/* Badge grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4"
        >
          {BADGES.map(({ Icon, title, desc, color, bg }) => (
            <motion.div
              key={title}
              variants={badgeVariants}
              className="group flex flex-col items-center text-center gap-3 p-5 rounded-2xl hover:shadow-card transition-shadow duration-300 cursor-default"
            >
              <div className={`w-14 h-14 rounded-2xl ${bg} ${color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                <Icon sx={{ fontSize: 28 }} />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 leading-tight">{title}</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-snug">{desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Review marquee */}
        <div className="mt-10 overflow-hidden py-4">
          <div className="flex gap-4 marquee-inner">
            {[...REVIEWS, ...REVIEWS].map((review, i) => (
              <div
                key={i}
                className="flex-shrink-0 bg-rose-50 rounded-2xl px-5 py-3.5 flex items-center gap-3 border border-rose-100"
                style={{ minWidth: "260px" }}
              >
                {/* Avatar initial */}
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {review.name[0]}
                </div>
                <div>
                  {/* Stars */}
                  <div className="flex items-center gap-0.5 mb-0.5">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <StarOutlinedIcon key={j} sx={{ fontSize: 12, color: "#fbbf24" }} />
                    ))}
                  </div>
                  <p className="text-xs text-gray-700 font-medium leading-snug">{review.review}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">— {review.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
